import React from "react";
import "./documentAnnotations.css";

export type DocumentAnnotationStatus = "success" | "warning" | "default";
/** Coordinates are fractions of the displayed, rotated page, measured from its top left. */
export interface DocumentAnnotation {
  id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  status: DocumentAnnotationStatus;
}

export function validDocumentAnnotation(value: DocumentAnnotation): boolean {
  return Number.isInteger(value.page) && value.page >= 1 && [value.x, value.y, value.width, value.height].every(Number.isFinite)
    && value.x >= 0 && value.y >= 0 && value.width > 0 && value.height > 0
    && value.x + value.width <= 1 && value.y + value.height <= 1;
}

/** Labels, semantic status and selection belong to the caller; the layer only renders locations. */
export function DocumentAnnotationLayer({ annotations, page, selectedId, onSelect }: {
  annotations: readonly DocumentAnnotation[]; page: number; selectedId?: string | null; onSelect?: (id: string) => void;
}) {
  const selectedRef = React.useRef<HTMLButtonElement | null>(null);
  const hasSelection = annotations.some(item => item.id === selectedId && item.page === page && validDocumentAnnotation(item));
  React.useEffect(() => {
    const target = selectedRef.current;
    if (!target) return;
    // Keep keyboard editing in view: scroll the document pane, never the surrounding form/window.
    for (let parent = target.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      if (parent.scrollHeight <= parent.clientHeight || !/auto|scroll/.test(getComputedStyle(parent).overflowY)) continue;
      const box = target.getBoundingClientRect(), viewport = parent.getBoundingClientRect();
      const top = box.top < viewport.top ? box.top - viewport.top : box.bottom > viewport.bottom ? box.bottom - viewport.bottom : 0;
      if (top) parent.scrollBy({ top });
      break;
    }
  }, [selectedId, page, hasSelection]);
  return <div className="mq-document-annotations">{annotations.filter(item => item.page === page && validDocumentAnnotation(item)).map((item, index) =>
    <button key={`${item.id}:${index}`} ref={item.id === selectedId ? selectedRef : undefined} type="button"
      className={`mq-document-annotation is-${item.status}${item.id === selectedId ? " is-selected" : ""}`}
      aria-label={item.label} title={item.label} aria-pressed={item.id === selectedId} onClick={() => onSelect?.(item.id)}
      style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%`, width: `${item.width * 100}%`, height: `${item.height * 100}%` }} />
  )}</div>;
}
